import { startLiveEars } from './liveEars';
import { startClipEars, canRecordClips } from './clipEars';
import { canCapture } from './capture';

/**
 * THE EARS, whichever this device and this app can have. Live where the app passes `getLiveSession` and the browser has
 * WebSockets and an AudioContext (the owner's panel: words appear while the person speaks); otherwise — and whenever the
 * live session cannot open (a token refused, a socket that will not connect, an in-app browser) — the clip ears, which
 * every customer has. One shape for both:
 *
 *   const ears = createEars({ getLiveSession, transcribe, continuous, onPartial, onFinal, onState, onError, onLevel })
 *   await ears.ready ; ears.kind                // 'live' | 'clip'
 *   ears.finish()  (hold released)  ears.cancel()  ears.pause()  ears.resume()  ears.close()
 */
export function createEars({ getLiveSession, transcribe, continuous = false, onPartial, onFinal, onState, onError, onLevel, deps = {} } = {}) {
  const liveOk = !!getLiveSession && (deps.liveSupported ?? canCapture());
  const clipOk = deps.clipSupported ?? canRecordClips();
  const make = deps.startClipEars || startClipEars;
  const makeLive = deps.startLiveEars || startLiveEars;
  let impl = null;
  let kind = liveOk ? 'live' : 'clip';
  let closed = false;
  let paused = false;
  let finishRequested = false; // a release asked for before the ears (or the fallback) were open

  const clip = () => make({ transcribe, continuous, onFinal, onState, onError, onLevel, deps: deps.clip });

  const ready = (async () => {
    if (liveOk) {
      let failedOpen = false;
      impl = makeLive({
        getSession: getLiveSession, continuous, onPartial, onFinal, onState, onLevel, recheck: transcribe,
        onError: (e) => {
          // Before it was listening, only a blocked microphone is final; any other trouble falls back to a clip.
          if (!failedOpen && (e.code === 'session' || e.code === 'socket') && clipOk) { failedOpen = true; return; }
          onError?.(e);
        },
        deps: deps.live,
      });
      try { await impl.ready; return; } catch (e) {
        try { impl.close(); } catch (_) { /* gone */ }
        impl = null;
        if (closed || !failedOpen || !clipOk) throw e;
        kind = 'clip';
      }
    }
    if (closed) return;
    impl = clip();
    if (paused) impl.pause?.();
    if (finishRequested) impl.stop?.();
    await impl.ready;
  })();
  ready.catch(() => {});

  return {
    ready,
    get kind() { return kind; },
    finish() { finishRequested = true; if (kind === 'live') impl?.commit(); else impl?.stop(); },
    cancel() { closed = true; if (kind === 'live') impl?.close(); else impl?.cancel(); },
    pause() { paused = true; impl?.pause?.(); },
    resume() { paused = false; impl?.resume?.(); },
    close() { closed = true; impl?.close(); },
  };
}
